import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Table, Tag, Tooltip, message } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitMenuDto, MenuDesignDto } from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { useRecoilValue } from "recoil";
import { locationsState } from "../../../../state/atoms";
import { DataKitMenuDrawer } from "./DataKitMenuDrawer";
import "./DataKitAccountMenusTab.scss";

interface Props {
  canWrite: boolean;
  onCountChange: (count: number) => void;
}

/**
 * The account-level Menus tab: one list every kit shares.
 *
 * Account-scoped rather than per-kit, which is what lets a rename propagate everywhere — a design
 * can place items from several kits, so a per-kit record would mean one design pointing at two
 * menus of the same name. The kit detail page has its own Menus tab, but that one is a read-only
 * usage view of this list.
 */
export function DataKitAccountMenusTab({ canWrite, onCountChange }: Props) {
  const locations = useRecoilValue(locationsState);
  const [menus, setMenus] = useState<DataKitMenuDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<DataKitMenuDto | null>(null);
  // Designs staged in the New Menu modal. A menu has to exist before a design can point at it, so
  // these are held here and attached after the create returns an id - see createMenu.
  const [candidates, setCandidates] = useState<MenuDesignDto[]>([]);
  const [staged, setStaged] = useState<MenuDesignDto[]>([]);
  const [pickedDesign, setPickedDesign] = useState<string | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    DataKitApi.listMenus()
      .then((res) => {
        const next = res.data || [];
        setMenus(next);
        onCountChange(next.length);
      })
      .catch(() => message.error("Could not load menus"))
      .finally(() => setLoading(false));
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCandidates = useCallback(() => {
    DataKitApi.listAttachableDesigns()
      .then((res) => setCandidates(res.data || []))
      .catch(() => undefined);
  }, []);

  /**
   * The design's venue, for the second line of each picker row. City/state rather than the
   * location's own name because that is what the mock shows ("DALLAS, TX") and what tells two
   * similarly-named design files apart; keyed on internalId, the id MenuDesignDto.locationId
   * carries, matching DataKitOverrideTables' own lookup.
   */
  const locationLabelById = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((l: { internalId?: number | null; name?: string; city?: string; state?: string }) => {
      if (l.internalId == null) return;
      const place = [l.city, l.state].filter(Boolean).join(", ");
      map.set(String(l.internalId), place || l.name || "");
    });
    return map;
  }, [locations]);

  /** Designs not already on a menu - attaching one here would otherwise silently move it. */
  const attachable = useMemo(
    () =>
      candidates.filter(
        (d) => !d.menuId && !staged.some((s) => s.designId === d.designId)
      ),
    [candidates, staged]
  );

  const openCreate = () => {
    setNewName("");
    setNewNote("");
    setStaged([]);
    setPickedDesign(undefined);
    loadCandidates();
    setCreateOpen(true);
  };

  const createMenu = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    DataKitApi.createMenu({ name, note: newNote.trim() || null })
      .then((res) => {
        const menuId = res.data?.id;
        // Attaching is a second call per design because a design points at a menu, not the other
        // way round - there is no menu id to point at until the create above has returned one.
        if (!menuId || staged.length === 0) return;
        return Promise.all(
          staged.map((d) => DataKitApi.setDesignMenu(d.designId, { menuId }))
        ).then(() => undefined);
      })
      .then(() => {
        setCreateOpen(false);
        setNewName("");
        setNewNote("");
        setStaged([]);
        message.success("Menu added");
        load();
      })
      .catch(() => message.error("Could not create that menu"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Menu",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitMenuDto, b: DataKitMenuDto) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      // The chip sits beside the name, not in the Designs column - that column keeps its 0 so the
      // three counts still read as a row of numbers.
      render: (_: string, menu: DataKitMenuDto) => (
        <span className="menus-name-cell">
          <span className="menus-name">{menu.name}</span>
          {menu.designCount === 0 && <Tag className="menus-no-designs">no designs yet</Tag>}
        </span>
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (note?: string | null) =>
        note ? <span className="menus-note">{note}</span> : <span className="menus-empty">--</span>,
    },
    {
      title: "Designs",
      dataIndex: "designCount",
      key: "designCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "Locations",
      dataIndex: "locationCount",
      key: "locationCount",
      width: 92,
      align: "center" as const,
    },
    {
      title: "",
      key: "action",
      width: 85,
      align: "right" as const,
      render: (_: unknown, menu: DataKitMenuDto) => (
        <Button className="menus-edit-btn" size="small" onClick={() => setEditing(menu)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="data-kit-account-menus-tab">
      <div className="menus-section-header">
        <span className="menus-eyebrow">Menus</span>
        <span className="menus-caption">
          Menus for your whole account. Attach designs to a menu to show it on your website and
          other channels.
        </span>
        {canWrite && (
          <Button
            className="menus-new-btn"
            size="small"
            onClick={openCreate}
          >
            + New Menu
          </Button>
        )}
      </div>

      <Table
        className="menus-table"
        dataSource={menus}
        columns={columns}
        rowKey={(menu) => menu.id || menu.name}
        loading={loading}
        pagination={false}
        locale={{ emptyText: "No menus yet" }}
      />

      <Modal
        className={"data-kit-menu-modal clean one"}
        open={createOpen}
        width={600}
        onCancel={() => setCreateOpen(false)}
      >
        <header>
          <h2>New Menu</h2>
        </header>
        <label className="data-kit-field">
          <span>Menu Name</span>
          <Input
            size="large"
            placeholder="Menu Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createMenu}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Menu Note</span>
          <Input.TextArea
            placeholder="Menu Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </label>

        <div className="menus-modal-divider" />

        <div className="data-kit-field">
          <span className="menus-designs-label">
            Designs on this Menu
            <Tooltip title="A menu shows on a channel through the designs attached to it. Items come from whatever those designs place.">
              <QuestionCircleOutlined className="menus-designs-help" />
            </Tooltip>
          </span>
          {staged.length === 0 && (
            <p className="menus-modal-note">
              None yet &mdash; this menu appears on no channel until a design points at it.
            </p>
          )}
          {staged.length > 0 && (
            <div className="menus-staged-list">
              {staged.map((design) => (
                <span className="menus-staged" key={design.designId}>
                  <span className="menus-staged-name">{design.designName}</span>
                  <button
                    type="button"
                    className="menus-staged-remove"
                    aria-label={`Remove ${design.designName}`}
                    onClick={() =>
                      setStaged((current) =>
                        current.filter((d) => d.designId !== design.designId)
                      )
                    }
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="menus-attach-row">
            <Select
              className="menus-attach-select"
              size="large"
              showSearch
              allowClear
              placeholder="Select a design to attach"
              value={pickedDesign}
              onChange={(value) => setPickedDesign(value)}
              // The label is a row, not a string, so filtering has to be told what to read.
              filterOption={(input, option) =>
                (option?.designName || "").toLowerCase().includes(input.toLowerCase())
              }
              options={attachable.map((d) => ({
                value: d.designId,
                designName: d.designName,
                label: (
                  <span className="menus-design-option">
                    <img
                      className="menus-design-thumb"
                      src={`${env.rendererPath}/design/thumbnail/${d.designId}?height=80`}
                      alt=""
                    />
                    <span className="menus-design-text">
                      <span className="menus-design-name">{d.designName}</span>
                      {(() => {
                        const place =
                          d.locationId == null
                            ? ""
                            : locationLabelById.get(String(d.locationId)) || "";
                        return place ? <span className="menus-design-loc">{place}</span> : null;
                      })()}
                    </span>
                  </span>
                ),
              }))}
              notFoundContent="No designs left to attach"
            />
            <Button
              size="large"
              disabled={!pickedDesign}
              onClick={() => {
                const design = attachable.find((d) => d.designId === pickedDesign);
                if (!design) return;
                setStaged((current) => [...current, design]);
                setPickedDesign(undefined);
              }}
            >
              Attach
            </Button>
          </div>
        </div>

        <footer>
          <Button size="large" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!newName.trim()}
            onClick={createMenu}
          >
            Create menu
          </Button>
        </footer>
      </Modal>

      {editing && (
        <DataKitMenuDrawer
          menu={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
